import React, { useEffect, useMemo, useState } from 'react';
import { LectureEventDetailsDialog } from './componenets/lecture-event-details-dialog';

export interface DiroCalendarProps {
  data: any[];
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
  loading?: boolean;
}

export const DiroCalendar: React.FC<DiroCalendarProps> = ({
  data,
  onDateRangeChange,
  loading,
}) => {
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [view, setView] = useState<'month' | 'week'>('month');
  const [displayEventDetailsDialog, setDisplayEventDetailsDialog] =
    useState(false);
  const [selectedEventForDetails, setSelectedEventForDetails] = useState<
    any | null
  >(null);

  // Week range (Sunday-Saturday) - matches the Hebrew/Israeli locale and the
  // א-ש header row rendered below in the RTL grid.
  const startOfWeek = useMemo(() => {
    const d = new Date(currentDate);
    const sundayOffset = d.getDay(); // Sunday=0 ... Saturday=6
    d.setDate(d.getDate() - sundayOffset);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [currentDate]);
  const endOfWeek = useMemo(() => {
    const d = new Date(startOfWeek);
    d.setDate(d.getDate() + 6);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [startOfWeek]);

  const startOfMonth = useMemo(() => {
    const d = new Date(currentDate);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [currentDate]);

  const endOfMonth = useMemo(() => {
    const d = new Date(currentDate);
    d.setMonth(d.getMonth() + 1);
    d.setDate(0);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [currentDate]);

  const rangeStart = view === 'week' ? startOfWeek : startOfMonth;
  const rangeEnd = view === 'week' ? endOfWeek : endOfMonth;

  // Notify parent on range change
  useEffect(() => {
    onDateRangeChange?.(rangeStart, rangeEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart.getTime(), rangeEnd.getTime(), view]);

  const goPrev = () => {
    const d = new Date(currentDate);
    if (view === 'week') {
      d.setDate(d.getDate() - 7);
    } else {
      d.setMonth(d.getMonth() - 1);
    }
    setCurrentDate(d);
  };
  const goNext = () => {
    const d = new Date(currentDate);
    if (view === 'week') {
      d.setDate(d.getDate() + 7);
    } else {
      d.setMonth(d.getMonth() + 1);
    }
    setCurrentDate(d);
  };
  const goToday = () => {
    setCurrentDate(new Date());
  };

  const getMonthMatrix = () => {
    const firstDay = new Date(startOfMonth);
    // Sunday-first to align with the א-ש header row rendered into the RTL grid.
    const startWeekday = firstDay.getDay(); // Sunday=0 ... Saturday=6
    const weeks: Date[][] = [];
    const current = new Date(firstDay);
    current.setDate(1 - startWeekday);
    for (let w = 0; w < 6; w++) {
      const week: Date[] = [];
      for (let d = 0; d < 7; d++) {
        week.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      weeks.push(week);
    }
    return weeks;
  };

  const isSameDay = (a: Date, b: Date) => {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  };

  const eventsByDate = (date: Date) => {
    const rows = Array.isArray(data) ? data : [];
    return rows.filter((ev) => {
      const s =
        ev._startTimeDate ??
        (ev.startTime ? new Date(ev.startTime) : undefined);
      if (!s) return false;
      return isSameDay(s, date);
    });
  };

  const periodLabel =
    view === 'week'
      ? `${startOfWeek.toLocaleDateString('he-IL', {
          day: '2-digit',
          month: '2-digit',
        })} - ${endOfWeek.toLocaleDateString('he-IL', {
          day: '2-digit',
          month: '2-digit',
        })}`
      : currentDate.toLocaleString('he-IL', {
          month: 'long',
          year: 'numeric',
        });

  const weeks = getMonthMatrix();
  const weekDays = useMemo(() => {
    const days: Date[] = [];
    const cur = new Date(startOfWeek);
    for (let i = 0; i < 7; i++) {
      days.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return days;
  }, [startOfWeek]);

  // Week view time filter and layout
  const [weekStartHour, setWeekStartHour] = useState<number>(8);
  const [weekEndHour, setWeekEndHour] = useState<number>(22);
  const sanitizedStartHour = Math.max(0, Math.min(23, weekStartHour));
  const sanitizedEndHour = Math.max(0, Math.min(23, weekEndHour));
  const startHour = Math.min(sanitizedStartHour, sanitizedEndHour);
  const endHour = Math.max(sanitizedStartHour, sanitizedEndHour);
  const displayedHours = useMemo(() => {
    const hours: number[] = [];
    for (let h = startHour; h <= endHour; h++) {
      hours.push(h);
    }
    return hours;
  }, [startHour, endHour]);
  const hourSlotHeight = 40;
  const weekColumnMinHeight = (displayedHours.length || 1) * hourSlotHeight;
  const dayHeaderHeight = 24;

  return (
    <div>
      <LectureEventDetailsDialog
        visible={displayEventDetailsDialog}
        onHide={() => {
          setDisplayEventDetailsDialog(false);
          setSelectedEventForDetails(null);
        }}
        event={selectedEventForDetails}
      />
      {/* Debug sample counts */}
      {(() => {
        try {
          const flatDays = getMonthMatrix().flat();
          const matches = flatDays.slice(0, 7).map((d) => ({
            date: d.toISOString().slice(0, 10),
            count: eventsByDate(d).length,
          }));
          // eslint-disable-next-line no-console
          console.log(
            '[prof-lectures][prof-calendar] day match sample:',
            matches,
          );
        } catch {}
        return null;
      })()}

      {/* Unscheduled banner */}
      {Array.isArray(data) && data.some((l) => !l?.startTime) && (
        <div
          style={{
            marginBottom: '12px',
            padding: '8px 12px',
            background: '#fff7ed',
            border: '1px solid #fed7aa',
            color: '#7c2d12',
            borderRadius: '6px',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '6px' }}>
            נמצאו מפגשים ללא תאריך מתוזמן
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {(data || [])
              .filter((l) => !l?.startTime)
              .slice(0, 10)
              .map((l) => (
                <span
                  key={l.id}
                  style={{
                    fontSize: '12px',
                    background: '#ffedd5',
                    border: '1px solid #fdba74',
                    color: '#7c2d12',
                    padding: '2px 6px',
                    borderRadius: '9999px',
                  }}
                  title={`${l.courseName ?? ''}${
                    l.lectureName ? ` - ${l.lectureName}` : ''
                  }`}
                >
                  {l.courseName ?? l.lectureName ?? `#${l.id}`}
                </span>
              ))}
            {(data || []).filter((l) => !l?.startTime).length > 10 && (
              <span style={{ fontSize: '12px' }}>ועוד...</span>
            )}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px',
        }}
      >
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={goPrev}
            title="חודש/שבוע קודם"
            aria-label="Previous"
            style={{
              height: '30px',
              width: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #d1d5db',
              background: '#f8fafc',
              color: '#111827',
              borderRadius: '9999px',
              cursor: 'pointer',
            }}
          >
            ‹
          </button>
          <button
            onClick={goToday}
            title="היום"
            aria-label="Today"
            style={{
              height: '30px',
              padding: '0 12px',
              border: '1px solid #d1d5db',
              background: '#e5e7eb',
              color: '#111827',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            היום
          </button>
          <button
            onClick={goNext}
            title="חודש/שבוע הבא"
            aria-label="Next"
            style={{
              height: '30px',
              width: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #d1d5db',
              background: '#f8fafc',
              color: '#111827',
              borderRadius: '9999px',
              cursor: 'pointer',
            }}
          >
            ›
          </button>
        </div>
        <div style={{ fontSize: '18px', fontWeight: 600 }}>{periodLabel}</div>
        <div style={{ minWidth: '60px', textAlign: 'right' }}>
          {loading ? 'טוען...' : ''}
        </div>
      </div>

      {/* View toggle + time filter */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
        <button
          onClick={() => setView('month')}
          style={{
            height: '28px',
            padding: '0 10px',
            background: view === 'month' ? '#e5e7eb' : '#fff',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          חודש
        </button>
        <button
          onClick={() => setView('week')}
          style={{
            height: '28px',
            padding: '0 10px',
            background: view === 'week' ? '#e5e7eb' : '#fff',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          שבוע
        </button>
        {view === 'week' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: '#374151' }}>
              טווח שעות:
            </span>
            <select
              value={startHour}
              onChange={(e) => setWeekStartHour(parseInt(e.target.value, 10))}
              style={{
                height: '28px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
              }}
            >
              {Array.from({ length: 24 }).map((_, h) => (
                <option key={`start-${h}`} value={h}>
                  {`${h.toString().padStart(2, '0')}:00`}
                </option>
              ))}
            </select>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>עד</span>
            <select
              value={endHour}
              onChange={(e) => setWeekEndHour(parseInt(e.target.value, 10))}
              style={{
                height: '28px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
              }}
            >
              {Array.from({ length: 24 }).map((_, h) => (
                <option key={`end-${h}`} value={h}>
                  {`${h.toString().padStart(2, '0')}:00`}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {view === 'month' ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px',
            direction: 'rtl',
          }}
        >
          {['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'].map((d) => (
            <div
              key={d}
              style={{
                textAlign: 'center',
                fontWeight: 600,
                padding: '6px 0',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              {d}
            </div>
          ))}
          {weeks.flat().map((day, idx) => {
            const inCurrentMonth = day.getMonth() === currentDate.getMonth();
            const dayEvents = eventsByDate(day);
            return (
              <div
                key={idx}
                style={{
                  minHeight: '100px',
                  minWidth: '120px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: inCurrentMonth ? '#ffffff' : '#fafafa',
                  padding: '6px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}
              >
                <div
                  style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>{day.getDate()}</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                >
                  {dayEvents.map((ev: any) => (
                    <div
                      key={ev.id}
                      style={{
                        fontSize: '12px',
                        background: '#eef2ff',
                        border: '1px solid #c7d2fe',
                        color: '#1e3a8a',
                        borderRadius: '4px',
                        padding: '2px 4px',
                        cursor: 'pointer',
                      }}
                      title={`${
                        (
                          ev._startTimeDate ??
                          (ev.startTime ? new Date(ev.startTime) : null)
                        )?.toLocaleTimeString('he-IL', {
                          hour: '2-digit',
                          minute: '2-digit',
                        }) ?? ''
                      } - ${
                        (
                          ev._endTimeDate ??
                          (ev.endTime ? new Date(ev.endTime) : null)
                        )?.toLocaleTimeString('he-IL', {
                          hour: '2-digit',
                          minute: '2-digit',
                        }) ?? ''
                      } • ${ev.courseName ?? ''}${
                        ev.lectureName ? ` - ${ev.lectureName}` : ''
                      }${
                        ev.additionalCourseName
                          ? ` (${ev.additionalCourseName})`
                          : ''
                      }${ev.className ? ` [${ev.className}]` : ''}`}
                      onClick={() => {
                        const start =
                          ev._startTimeDate ??
                          (ev.startTime ? new Date(ev.startTime) : undefined);
                        const end =
                          ev._endTimeDate ??
                          (ev.endTime ? new Date(ev.endTime) : undefined);
                        setSelectedEventForDetails({
                          title:
                            ev.lectureName ??
                            ev.courseName ??
                            ev.additionalCourseName ??
                            'מפגש',
                          start,
                          end,
                          lectureId: ev.id,
                          courseName: ev.courseName,
                          additionalCourseName: ev.additionalCourseName,
                          className: ev.className,
                          isRemote: ev.isRemote,
                        });
                        setDisplayEventDetailsDialog(true);
                      }}
                    >
                      <span>
                        {(
                          ev._startTimeDate ??
                          (ev.startTime ? new Date(ev.startTime) : null)
                        )?.toLocaleTimeString('he-IL', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                        {' - '}
                        {(
                          ev._endTimeDate ??
                          (ev.endTime ? new Date(ev.endTime) : null)
                        )?.toLocaleTimeString('he-IL', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                      {' • '}
                      <span>
                        {ev.courseName
                          ? ev.lectureName
                            ? `${ev.courseName} - ${ev.lectureName}`
                            : ev.courseName
                          : (ev.lectureName ?? 'מפגש')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '80px repeat(7, 1fr)',
            gap: '4px',
            direction: 'rtl',
          }}
        >
          {/* Header: empty gutter then weekdays */}
          <div />
          {['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'].map((d, idx) => (
            <div
              key={d + idx}
              style={{
                textAlign: 'center',
                fontWeight: 600,
                padding: '6px 0',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              {d}
            </div>
          ))}
          {/* Time gutter with hours */}
          <div
            style={{
              border: '1px solid #e5e7eb',
              backgroundColor: '#ffffff',
              padding: '6px',
              minWidth: '80px',
            }}
          >
            {/* Spacer to align with per-day header height */}
            <div style={{ height: `${dayHeaderHeight}px` }} />
            {displayedHours.map((h) => (
              <div
                key={h}
                style={{
                  height: `${hourSlotHeight}px`,
                  borderTop: h === 0 ? 'none' : '1px dashed #e5e7eb',
                  fontSize: '11px',
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'flex-start',
                }}
              >
                {`${h.toString().padStart(2, '0')}:00`}
              </div>
            ))}
            <div
              style={{
                height: '0px',
                fontSize: '11px',
                color: '#6b7280',
              }}
              title="00:00"
            />
          </div>
          {/* 7 day columns with hour stripes + positioned events */}
          {weekDays.map((day, idx) => {
            const dayEvents = eventsByDate(day);
            return (
              <div
                key={idx}
                style={{
                  minHeight: `${weekColumnMinHeight}px`,
                  minWidth: '120px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  padding: '6px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}
              >
                {/* Day header */}
                <div
                  style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    display: 'flex',
                    justifyContent: 'space-between',
                    height: `${dayHeaderHeight}px`,
                    alignItems: 'flex-end',
                  }}
                >
                  <span>
                    {day.getDate().toString().padStart(2, '0')}/
                    {(day.getMonth() + 1).toString().padStart(2, '0')}
                  </span>
                </div>
                {/* Hour stripes + positioned events */}
                <div
                  style={{
                    position: 'relative',
                    height: `${weekColumnMinHeight}px`,
                  }}
                >
                  {/* Hour stripes background */}
                  {displayedHours.map((h) => (
                    <div
                      key={h}
                      style={{
                        height: `${hourSlotHeight}px`,
                        borderTop: h === 0 ? 'none' : '1px dashed #f3f4f6',
                      }}
                    />
                  ))}
                  {/* Positioned events */}
                  {dayEvents.length === 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: `0px`,
                        fontSize: '12px',
                        color: '#9ca3af',
                        textAlign: 'center',
                        padding: '8px 0',
                      }}
                    >
                      אין מפגשים
                    </div>
                  )}
                  {dayEvents.map((ev: any) => {
                    const s: Date | undefined =
                      ev._startTimeDate ??
                      (ev.startTime ? new Date(ev.startTime) : undefined);
                    const e: Date | undefined =
                      ev._endTimeDate ??
                      (ev.endTime ? new Date(ev.endTime) : undefined);
                    const sMinutes = s
                      ? s.getHours() * 60 + s.getMinutes()
                      : startHour * 60;
                    const eMinutes = e
                      ? e.getHours() * 60 + e.getMinutes()
                      : sMinutes + 30;
                    const startOffsetMin = Math.max(
                      0,
                      sMinutes - startHour * 60,
                    );
                    const endOffsetMin = Math.min(
                      (endHour + 1) * 60 - startHour * 60,
                      eMinutes - startHour * 60,
                    );
                    const durationMin = Math.max(
                      20,
                      endOffsetMin - startOffsetMin,
                    );
                    const topPx = (startOffsetMin / 60) * hourSlotHeight;
                    const heightPx = (durationMin / 60) * hourSlotHeight;
                    return (
                      <div
                        key={ev.id}
                        style={{
                          position: 'absolute',
                          left: '4px',
                          right: '4px',
                          top: `${topPx}px`,
                          height: `${heightPx}px`,
                          fontSize: '12px',
                          background: '#eef2ff',
                          border: '1px solid #c7d2fe',
                          color: '#1e3a8a',
                          borderRadius: '6px',
                          padding: '2px 6px',
                          cursor: 'pointer',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                        title={`${
                          s?.toLocaleTimeString('he-IL', {
                            hour: '2-digit',
                            minute: '2-digit',
                          }) ?? ''
                        } - ${
                          e?.toLocaleTimeString('he-IL', {
                            hour: '2-digit',
                            minute: '2-digit',
                          }) ?? ''
                        } • ${ev.courseName ?? ''}${
                          ev.lectureName ? ` - ${ev.lectureName}` : ''
                        }${
                          ev.additionalCourseName
                            ? ` (${ev.additionalCourseName})`
                            : ''
                        }${ev.className ? ` [${ev.className}]` : ''}`}
                        onClick={() => {
                          setSelectedEventForDetails({
                            title:
                              ev.courseName ??
                              ev.lectureName ??
                              ev.additionalCourseName ??
                              'מפגש',
                            start: s,
                            end: e,
                            lectureId: ev.id,
                            courseName: ev.courseName,
                            additionalCourseName: ev.additionalCourseName,
                            className: ev.className,
                            isRemote: ev.isRemote,
                          });
                          setDisplayEventDetailsDialog(true);
                        }}
                      >
                        <span style={{ whiteSpace: 'nowrap' }}>
                          {s?.toLocaleTimeString('he-IL', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}{' '}
                          -{' '}
                          {e?.toLocaleTimeString('he-IL', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        <span style={{ whiteSpace: 'nowrap' }}>
                          •{' '}
                          {ev.courseName
                            ? ev.lectureName
                              ? `${ev.courseName} - ${ev.lectureName}`
                              : ev.courseName
                            : (ev.lectureName ?? 'מפגש')}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
